import { User } from 'firebase/auth';
import { partition } from 'lodash';
import { ContentData } from 'types/contentTypes';
import SectionData from 'types/SectionData';

import ContentService from './contentService';
import EnrollmentService from './enrollmentService';
import SectionService from './sectionService';
import { StatService } from './statService';
import SkillApi, { NewSkillData } from '../api/skillApi';
import SkillData from '../types/skillData';
import { removeOrderableWithId } from '../utils/functions';

export default class SkillService {
  public static async getViewableSkills() {
    return (await SkillApi.getSkills()).filter(skill => skill.isPublished);
  }

  public static async addSkillAtEnd(newSkill: Omit<NewSkillData, 'order'>) {
    const skills = await SkillApi.getSkills();
    return await SkillApi.addSkill({ ...newSkill, order: skills.length + 1 });
  }

  public static async removeSkillFromArray(skillToDelete: SkillData, skills: SkillData[]) {
    await SkillApi.removeSkill(skillToDelete.id);
    const updatedSkills = removeOrderableWithId(skillToDelete, skills);
    await SkillApi.updateSkills(updatedSkills);
    return updatedSkills;
  }

  public static async toggleSkillIsPublishedFromArray(skillToUpdate: SkillData, skills: SkillData[]) {
    const updatedSkills = skills.map(skill => {
      if (skill.id === skillToUpdate.id) {
        return { ...skill, isPublished: !skill.isPublished };
      } else {
        return skill;
      }
    });
    await SkillApi.updateSkills(updatedSkills);
    return updatedSkills;
  }

  public static async getFirstIncompleteSectionAndContent(
    skill: SkillData,
    user: User
  ): Promise<(SectionData | ContentData | undefined)[]> {
    const firstIncompleteSection = await SectionService.getFirstIncompleteSection(skill, user);
    if (!firstIncompleteSection) return [undefined, undefined];
    const firstIncompleteContent = await ContentService.getFirstIncompleteContent(firstIncompleteSection, user);
    return [firstIncompleteContent, firstIncompleteSection];
  }

  public static async getCompletedInProgressAndRestSkills(user: User | null) {
    const skills = await this.getViewableSkills();

    const completedSkillIds = user ? await StatService.getCompletedSkillIds(user.uid) : [];
    const [completedSkills, completedRest] = partition(skills, skill => completedSkillIds.includes(skill.id));

    const enrolledSkillIds = await EnrollmentService.getEnrolledSkillIds(user);
    const [inProgressSkills, rest] = partition(completedRest, skill => enrolledSkillIds.includes(skill.id));

    return [completedSkills, inProgressSkills, rest];
  }

  /*  public static async getCompletedSkillsForUser(user: User): Promise<string[]> {
    const completedSkillsId await StatService.getCompletedSkillIdsForUser(user.uid);
    return completedSkillsId;
  }*/
}
